// Обложка курса — общая форма для курса (`study_plans`) и шаблона
// (`course_templates`).
//
// Живёт в `_shared`, а не в contracts/courses: обложку показывают три
// поверхности из разных модулей (список курсов, единый каталог, шапка
// воркспейса), и вторая копия схемы неизбежно разошлась бы по набору полей —
// например «в каталоге есть thumbHash, а в списке нет», и blur-up работал бы
// через страницу.
import { z } from "zod";

/**
 * Откуда взялась обложка.
 *  - `generated` — нарисована image-моделью по промпту;
 *  - `uploaded`  — автор загрузил свою картинку.
 *
 * Различие видимо в UI: у сгенерированной есть «сгенерировать заново», у
 * загруженной — «удалить и сгенерировать».
 */
export const COVER_SOURCES = ["generated", "uploaded"] as const;
export const coverSourceSchema = z.enum(COVER_SOURCES);
export type CoverSource = (typeof COVER_SOURCES)[number];

/**
 * Состояние обложки.
 *
 * `null` (обложки нет и не заказывали) намеренно отличается от `pending`:
 * карточка без этого различия не знает, показывать скелетон или сразу
 * иконку-фолбэк, и у половины курсов мигал бы вечный «сейчас загрузится».
 */
export const COVER_STATUSES = ["pending", "ready", "failed"] as const;
export const coverStatusSchema = z.enum(COVER_STATUSES);
export type CoverStatus = (typeof COVER_STATUSES)[number];

/**
 * Обложка в выдаче API.
 *
 * `url` — готовый публичный адрес прокси-роута, а не presigned: обложки
 * каталога обязаны кэшироваться браузером. `null` во всех полях = обложки нет,
 * поверхность рисует иконку-плитку.
 */
export const courseCoverSchema = z.object({
	url: z.string().max(2000).nullable(),
	/** ThumbHash base64 — blur-up до загрузки картинки. */
	thumbHash: z.string().max(120).nullable(),
	source: coverSourceSchema.nullable(),
	status: coverStatusSchema.nullable(),
});
export type CourseCover = z.infer<typeof courseCoverSchema>;

/** Обложки нет — единственная форма «пустого» значения на всех поверхностях. */
export const EMPTY_COVER: CourseCover = {
	url: null,
	thumbHash: null,
	source: null,
	status: null,
};

/**
 * Строка БД → DTO обложки.
 *
 * Один маппер на курс и на шаблон: набор колонок у них одинаковый по решению
 * миграции 0114, и две копии проекции разъехались бы на первом же новом поле.
 */
export function toCourseCover(row: {
	coverUrl: string | null;
	coverThumbHash: string | null;
	coverSource: string | null;
	coverStatus: string | null;
}): CourseCover {
	const source = (COVER_SOURCES as readonly string[]).includes(row.coverSource ?? "")
		? (row.coverSource as CoverSource)
		: null;
	const status = (COVER_STATUSES as readonly string[]).includes(row.coverStatus ?? "")
		? (row.coverStatus as CoverStatus)
		: null;
	return {
		url: row.coverUrl,
		thumbHash: row.coverThumbHash,
		source,
		// Готовая обложка без URL — невозможное состояние; отдать `ready` с
		// пустым адресом значило бы заставить UI рисовать битую картинку.
		status: status === "ready" && !row.coverUrl ? "failed" : status,
	};
}
